import React, {useMemo} from 'react';
import {NAVIGATION_STATUSES, useReservationSettings} from 'hooks';
import {MODAL_FORMS} from './constants';
import {getAgeLimitByCountry} from 'utils/reservationSettings/utils';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useGetReservationType} from 'hooks/useGetReservationType';
import {ReservationSettingsModal} from './ReservationSettingsModal';
import {useModalControls} from '@chekin/guestapp-ui-kit';

type UseReservationSettingsModalProps = {
  showFirstTime?: boolean;
  openModalPerSession?: boolean;
};

export function useReservationSettingsModal({
  openModalPerSession,
  showFirstTime,
}: UseReservationSettingsModalProps = {}) {
  const {data} = useSummary();
  const {housing, data: reservation, hasAvantio} = useReservation();
  const {checkCanEditReservationDetails} = useReservationSettings();
  const {hasReservationGroup, isLoaded} = useGetReservationType();

  const {
    isOpen: isOpenConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
  } = useModalControls();

  const countryCode = data?.country_code;
  const isActiveTaxes = data?.taxes?.active;
  const hasAvantioConfirmLeadGuestForm = Boolean(
    reservation &&
      reservation?.show_invoicing_fields === null &&
      isLoaded &&
      reservation?.send_lead_guest_data_on_client &&
      hasAvantio,
  );

  const availableForms = useMemo(() => {
    const isNumberOfGuestsEditable = checkCanEditReservationDetails();

    return {
      [MODAL_FORMS.numberOfGuests]: !!isNumberOfGuestsEditable,
      [MODAL_FORMS.confirmLeaderGuest]: hasAvantioConfirmLeadGuestForm,
      [MODAL_FORMS.reservationType]: hasReservationGroup,
    };
  }, [
    hasReservationGroup,
    checkCanEditReservationDetails,
    hasAvantioConfirmLeadGuestForm,
  ]);

  const isModalAvailable = Object.values(availableForms).some(Boolean);
  const leadGuestFormCompleted =
    reservation?.show_invoicing_fields !== null ||
    reservation?.show_invoicing_fields === undefined;
  // TODO i have to make this comparation, cuz from BE side i only have 3 states
  //true
  //false
  //null
  // and true and false means the form is completed, so i cant use !reservation?.show_invoicing_fields as a condition
  //this is a temporal fix, this must be fixed from BE side to return enum value
  const validateGuestAge = React.useMemo(() => {
    if (isActiveTaxes || !countryCode) {
      return {ageLimit: null};
    }

    return getAgeLimitByCountry(countryCode, housing?.police_account_type);
  }, [countryCode, isActiveTaxes, housing]);

  React.useEffect(() => {
    if (isLoaded) {
      const isProcessComplete =
        data?.guest_registration?.status === NAVIGATION_STATUSES.complete;

      if (hasAvantioConfirmLeadGuestForm) {
        openConfirmModal();

        return;
      }
      if (showFirstTime) {
        if (openModalPerSession && !isProcessComplete && !leadGuestFormCompleted) {
          openConfirmModal();
          return;
        }
      }
    }
  }, [
    openModalPerSession,
    openConfirmModal,
    data?.guest_registration?.status,
    showFirstTime,
    hasAvantioConfirmLeadGuestForm,
    isLoaded,
    leadGuestFormCompleted,
  ]);

  const Modal = React.useMemo(
    () => (
      <ReservationSettingsModal
        isOpen={isOpenConfirmModal && isModalAvailable}
        onClose={closeConfirmModal}
        availableForms={availableForms}
        ageLimit={validateGuestAge.ageLimit}
      />
    ),
    [
      isOpenConfirmModal,
      closeConfirmModal,
      availableForms,
      validateGuestAge.ageLimit,
      isModalAvailable,
    ],
  );

  return {
    isOpen: isOpenConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
    ageLimit: validateGuestAge.ageLimit,
    isModalAvailable,
    Modal,
    hasAvantioConfirmLeadGuestForm,
  };
}
